import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User } from 'firebase/auth';
import { onAuthChange, logoutUser, getUserProfile, createDefaultUserProfile, isRegistrationInProgress, auth, db } from '../firebase';
import { isOwnerEmail } from '../config/owner';
import { UserRole, Permissions, getDefaultPermissions } from '../domain/permissions';
// Remove the import for useLocation
// import { useLocation } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import { Subscription } from '../types/subscriptionTypes';
import { Box, CircularProgress, Typography } from '@mui/material';

// Define UserProfile interface
export interface UserProfile {
  uid: string;
  email: string;
  role: UserRole;
  name: string;
  firstName?: string;
  lastName?: string;
  photoURL?: string;
  department?: string;
  phone?: string;
  address?: string;
  title?: string;
  bio?: string;
  skills?: string[];
  socialLinks?: {
    linkedin?: string;
    twitter?: string;
    github?: string;
    website?: string;
  };
  preferences?: {
    darkMode?: boolean;
    notifications?: boolean;
    emailAlerts?: boolean;
    theme?: string;
    language?: string;
  };
  permissions?: Permissions;
  company?: string;
  position?: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  friends?: string[]; // Array of user IDs who are friends
  friendRequests?: string[]; // Array of user IDs who sent friend requests
  sentRequests?: string[]; // Array of user IDs to whom friend requests were sent
  subscription?: Subscription;
  payrollSettings?: {
    frequency: 'weekly' | 'biweekly' | 'monthly';
    nextPayrollDate?: string; // ISO date string
    autoProcess: boolean;
  };
  paymentMethod?: {
    type: 'credit_card' | 'bank_account';
    lastFour: string;
    expiryDate?: string;
  };
  manager?: string;
  directReports?: string[];
  employmentStatus?: 'full_time' | 'part_time' | 'contract' | 'temporary';
  hireDate?: Date;
  terminationDate?: Date;
  salary?: number;
  hourlyRate?: number;
  overtimeRate?: number;
  vacationDays?: number;
  sickDays?: number;
  personalDays?: number;
  benefits?: {
    health?: boolean;
    dental?: boolean;
    vision?: boolean;
    life?: boolean;
    disability?: boolean;
    retirement?: boolean;
  };
  documents?: {
    id: string;
    name: string;
    type: string;
    url: string;
    uploadDate: Date;
  }[];
  notes?: string;
}

// Role + permission model live in a single source of truth (imported at the top of
// this file). Re-exported here so existing `import { UserRole, Permissions,
// getDefaultPermissions } from '../contexts/AuthContext'` call sites keep working.
export type { UserRole, Permissions } from '../domain/permissions';
export { getDefaultPermissions } from '../domain/permissions';

// Define the type for the context value
interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  isPersistentLogin: boolean;
  loading: boolean;
  logout: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  updatePersistentLogin: (value: boolean) => void;
  // Add missing properties
  hasPermission: (permission: keyof Permissions) => boolean;
  isAdmin: boolean;
  isOwner: boolean;
  isManager: boolean;
  bypassAuthCheck: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  userProfile: null,
  isPersistentLogin: false,
  loading: true,
  logout: async () => {},
  refreshUserProfile: async () => {},
  updatePersistentLogin: () => {},
  hasPermission: () => false,
  isAdmin: false,
  isOwner: false,
  isManager: false,
  bypassAuthCheck: false,
  isLoading: true,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPersistentLogin, setPersistentLogin] = useState(false);
  const [profileListener, setProfileListener] = useState<(() => void) | null>(null);
  const [initializationComplete, setInitializationComplete] = useState(false);
  const [initError, setInitError] = useState<string | undefined>();
  const [initializationSteps, setInitializationSteps] = useState<string[]>([]);

  // Compute helper values for role and permissions
  // Owner status is granted by role 'Owner' OR by a hardcoded owner email
  // (kept in sync with firestore.rules' isPlatformOwner). The email path means
  // the platform owner gets god-mode even if their stored profile role differs.
  // `isOwner` flag is set by the setOwnerClaim function (mirrors the auth custom
  // claim) so owners delegated via claims — not just the bootstrap email — get the tier.
  const isOwner =
    (userProfile as any)?.isOwner === true ||
    userProfile?.role === 'Owner' ||
    isOwnerEmail(currentUser?.email);
  const isAdmin = userProfile?.role === 'Administrator';
  const isManager = userProfile?.role === 'Manager' || isAdmin;
  const bypassAuthCheck = false;

  // Check if user has a specific permission
  const hasPermission = (permission: keyof Permissions): boolean => {
    // Platform owner (role Owner or hardcoded email) has every permission.
    if (isOwner) return true;
    if (!userProfile) return false;

    // Administrators have all permissions
    if (userProfile.role === 'Administrator') return true;

    // Fall back to role defaults when a profile has no explicit permissions map
    // (signup doesn't store one), so role-based access works for everyone.
    const perms = userProfile.permissions || getDefaultPermissions((userProfile.role as UserRole) || 'Guest');
    return !!perms[permission];
  };
  
  // Function to refresh user profile on demand
  const refreshUserProfile = async () => {
    if (currentUser) {
      try {
        const profileResult = await getUserProfile(currentUser.uid);
        if (profileResult.success && profileResult.data) {
          setUserProfile(profileResult.data as UserProfile);
          
          // Update cached profile if persistent login is enabled
          if (isPersistentLogin) {
            localStorage.setItem('userProfile', JSON.stringify(profileResult.data));
          }
        }
      } catch (error) {
        console.error("Error refreshing user profile:", error);
        setInitError("Failed to refresh user profile");
      }
    }
  };

  // Handle logout
  const logout = async () => {
    try {
      setLoading(true);
      // Clean up listener first to prevent state updates after unmount
      if (profileListener) {
        profileListener();
        setProfileListener(null);
      }
      
      // Then logout from Firebase
      await logoutUser();
      
      // Then clear local state
      setCurrentUser(null);
      setUserProfile(null);
      setPersistentLogin(false);
      
      // Clear cached data
      localStorage.removeItem('userProfile');
      localStorage.removeItem('persistentLogin');
      localStorage.removeItem('authUser');
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Persistent-login preference. Firebase SDK persistence is configured at sign-in
  // time; this flag is just a local UI hint, never the source of truth for auth.
  const updatePersistentLogin = (value: boolean) => {
    setPersistentLogin(value);
    if (value) {
      localStorage.setItem('persistentLogin', 'true');
    } else {
      localStorage.removeItem('persistentLogin');
      localStorage.removeItem('authUser');
      localStorage.removeItem('userProfile');
    }
  };

  // Set up auth state listener and try to restore session
  useEffect(() => {
    let isMounted = true;
    let authUnsubscribe: (() => void) | null = null;
    
    const addInitStep = (step: string) => {
      if (isMounted) {
        setInitializationSteps(prev => [...prev, step]);
      }
    };

    const initialize = async () => {
      try {
        setLoading(true);
        addInitStep('Starting authentication initialization...');

        // If a native mobile shell injected a custom token, redeem it before
        // wiring up the auth listener so the WebView signs in as the same user
        // that authenticated natively. The token is single-use; clear it.
        const mobileToken = (window as any).__SUPERIOR_MOBILE_AUTH_TOKEN__;
        if (typeof mobileToken === 'string' && mobileToken.length > 0) {
          delete (window as any).__SUPERIOR_MOBILE_AUTH_TOKEN__;
          try {
            await signInWithCustomToken(auth, mobileToken);
            addInitStep('Signed in via mobile bridge');
          } catch (err) {
            addInitStep(`Mobile bridge sign-in failed: ${(err as Error).message}`);
          }
        }

        // Check localStorage for persistent login preference
        const persistentLogin = localStorage.getItem('persistentLogin') === 'true';
        if (isMounted) {
          setPersistentLogin(persistentLogin);
          addInitStep(`Persistent login preference: ${persistentLogin ? 'enabled' : 'disabled'}`);
        }

        // Set up Firebase auth state listener
        addInitStep('Setting up authentication listener...');
        authUnsubscribe = onAuthStateChanged(auth, async (user) => {
          if (!isMounted) return;
          
          addInitStep(`Auth state changed: ${user ? 'User logged in' : 'No user'}`);
          setLoading(true);
          setInitError(undefined);
          
          try {
            if (user) {
              setCurrentUser(user);
              addInitStep('Fetching user profile...');
              
              const profileResult = await getUserProfile(user.uid);
              if (profileResult.success && profileResult.data) {
                setUserProfile(profileResult.data as UserProfile);
                addInitStep('User profile fetched successfully');
              } else if (!profileResult.success && profileResult.notFound) {
                if (isRegistrationInProgress()) {
                  // A signup is writing this user's real profile right now. Don't
                  // seed a default Employee profile — that would race and block the
                  // Administrator's role/company write. The profile loads on sign-in.
                  addInitStep('Registration in progress; deferring profile creation');
                } else {
                  // Genuinely missing doc — safe to seed a default Employee profile.
                  addInitStep('Creating default user profile...');
                  const defaultProfile = await createDefaultUserProfile(user.uid, user.email || '');
                  if (defaultProfile.success) {
                    setUserProfile(defaultProfile.data as UserProfile);
                    addInitStep('Default profile created successfully');
                  } else {
                    throw new Error('Failed to create default profile');
                  }
                }
              } else {
                // Permission denied or transient error — surface it; do not silently overwrite.
                throw new Error((profileResult as { error: string }).error);
              }
              
              // Firebase SDK handles its own persistence — don't mirror auth state to localStorage.
            } else {
              setCurrentUser(null);
              setUserProfile(null);
              addInitStep('User signed out, clearing state');
              localStorage.removeItem('authUser');
              localStorage.removeItem('userProfile');
            }
          } catch (error) {
            console.error('Error in auth state change:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            setInitError(`Authentication error: ${errorMessage}`);
            addInitStep(`Error: ${errorMessage}`);
          } finally {
            if (isMounted) {
              setLoading(false);
              setInitializationComplete(true);
              addInitStep('Authentication initialization complete');
            }
          }
        });
      } catch (error) {
        console.error('Error during auth initialization:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        if (isMounted) {
          setInitError(`Failed to initialize authentication: ${errorMessage}`);
          addInitStep(`Fatal error: ${errorMessage}`);
          setLoading(false);
          setInitializationComplete(true);
        }
      }
    };
    
    initialize();
    
    return () => {
      isMounted = false;
      if (authUnsubscribe) {
        authUnsubscribe();
      }
      if (profileListener) {
        profileListener();
      }
    };
  }, []);

  // Provide loading screen while initializing
  if (!initializationComplete) {
    return (
      <Box 
        sx={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'white',
          zIndex: 9999,
          p: 3,
        }}
      >
        {initError ? (
          <>
            <Typography variant="h6" color="error" gutterBottom>
              Authentication Error
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 600, mb: 2 }}>
              {initError}
            </Typography>
            <Box sx={{ 
              maxHeight: '200px', 
              overflowY: 'auto', 
              width: '100%', 
              maxWidth: 600,
              mb: 2,
              p: 2,
              bgcolor: '#f5f5f5',
              borderRadius: 1
            }}>
              <Typography variant="body2" component="div">
                Initialization steps:
                {initializationSteps.map((step, index) => (
                  <Box key={index} sx={{ pl: 2, py: 0.5 }}>
                    • {step}
                  </Box>
                ))}
              </Typography>
            </Box>
            <button 
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 16px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </>
        ) : (
          <>
            <CircularProgress size={50} sx={{ mb: 3 }} />
            <Box sx={{ 
              maxWidth: 600,
              width: '100%',
              textAlign: 'center'
            }}>
              <Typography variant="h6" gutterBottom>
                Initializing Authentication
              </Typography>
              <Box sx={{ 
                maxHeight: '200px', 
                overflowY: 'auto',
                width: '100%',
                textAlign: 'left',
                p: 2
              }}>
                {initializationSteps.map((step, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      py: 0.5,
                      color: index === initializationSteps.length - 1 ? 'primary.main' : 'text.secondary'
                    }}
                  >
                    <span style={{ marginRight: '8px' }}>
                      {index === initializationSteps.length - 1 ? '→' : '✓'}
                    </span>
                    {step}
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}
      </Box>
    );
  }

  const value = {
    currentUser,
    userProfile,
    isPersistentLogin,
    loading,
    logout,
    refreshUserProfile,
    updatePersistentLogin,
    hasPermission,
    isAdmin,
    isOwner,
    isManager,
    bypassAuthCheck,
    isLoading: loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext; 